import type { Position } from 'geojson';
import type { Pieces, TraceTrack } from './types';
import { closeRings, pointOnTrack } from './utils';

/** How far (px) a trace must be dragged past a stop before it moves on. */
export const TRACE_STOP_PX = 40;

/** Stops close to where the trace joins a ring (px) would only hold it there. */
const JOIN_PX = 4;

/**
 * Where segment a→b meets segment c→d, as the fraction of the way along a→b.
 *
 * @param {Position[]} ab - The first segment.
 * @param {Position[]} cd - The second segment.
 * @return {number | null} The fraction, or null when they don't meet.
 */
const segmentCrossing = (
  [a, b]: Position[],
  [c, d]: Position[]
): number | null => {
  const rx = b[0] - a[0];
  const ry = b[1] - a[1];
  const sx = d[0] - c[0];
  const sy = d[1] - c[1];
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-18) return null;
  const qx = c[0] - a[0];
  const qy = c[1] - a[1];
  const t = (qx * sy - qy * sx) / den;
  const u = (qx * ry - qy * rx) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? t : null;
};

/**
 * Where a trace pauses along a ring: at each of its corners, and wherever it
 * crosses the edited area's boundary.
 *
 * @param {TraceTrack} track - The ring being followed.
 * @param {Pieces} pieces - The edited area's parts, open.
 * @return {number[]} The stops' arc positions, ascending.
 */
export const traceStops = (track: TraceTrack, pieces: Pieces): number[] => {
  const stops = track.cum.slice(0, -1);
  const rings = closeRings(pieces);
  for (let n = 0; n < track.positions.length - 1; n += 1) {
    const segment = [track.positions[n], track.positions[n + 1]];
    const length = track.cum[n + 1] - track.cum[n];
    for (const ring of rings) {
      for (let m = 0; m < ring.length - 1; m += 1) {
        const t = segmentCrossing(segment, [ring[m], ring[m + 1]]);
        if (t !== null && t > 0 && t < 1) stops.push(track.cum[n] + t * length);
      }
    }
  }
  return stops.sort((x, y) => x - y);
};

/** A ring's stops, and where the trace joined it. */
export interface RingStops {
  stops: number[];
  /** The ring's length. */
  total: number;
  /** Where the trace joined the ring. */
  start: number;
}

/**
 * The stop a run meets first on its way from one point to another, as a run
 * (signed, from where the trace joined the ring).
 *
 * @param {RingStops} ring - The ring's stops.
 * @param {number} from - The run so far; a stop right here counts.
 * @param {number} to - The run the pointer asks for.
 * @param {number | null} skip - A stop already passed, which no longer holds.
 * @return {number | null} The stop, or null when there is none between.
 */
export const stopBetween = (
  { stops, total, start }: RingStops,
  from: number,
  to: number,
  skip: number | null
): number | null => {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  let best: number | null = null;
  for (const stop of stops) {
    const base = stop - start;
    for (
      let k = Math.ceil((lo - base) / total);
      base + k * total <= hi;
      k += 1
    ) {
      const run = base + k * total;
      const skipped = skip !== null && Math.abs(run - skip) < 1e-12;
      if (
        Math.abs(run) > 1e-12 &&
        !skipped &&
        (best === null || Math.abs(run - from) < Math.abs(best - from))
      ) {
        best = run;
      }
    }
  }
  return best;
};

/** Holds a trace at each stop on the ring it follows. */
export interface TraceStopper {
  /** Starts on a ring, where the trace joined it. */
  join: (
    track: TraceTrack,
    start: number,
    entry: Position,
    pieces: Pieces
  ) => void;
  /** Moves toward the pointer's run, holding at the first stop on the way. */
  settle: (run: number, pointer: Position) => void;
  /** On release, lands on a stop within reach of the pointer. */
  land: (pointer: Position) => void;
  /** How far the trace shows the run. */
  shown: () => number;
}

/**
 * The stops for a trace, as the mock has them: the run pauses at each corner of
 * the ring and where it crosses the edited area, until the drag goes
 * TRACE_STOP_PX further, and a release near one lands exactly on it.
 *
 * @param {Function} pixelsApart - Screen distance between two map positions.
 * @return {TraceStopper} The stopper.
 */
export function traceStopper(
  pixelsApart: (a: Position, b: Position) => number
): TraceStopper {
  let track: TraceTrack | null = null;
  let ring: RingStops = { stops: [], total: 0, start: 0 };
  let shown = 0;
  // The last stop dragged past; it holds again once the drag turns back.
  let passed: number | null = null;
  let passedDirection = 0;

  /**
   * How far a run's tip lies from the pointer.
   *
   * @param {number} run - The run.
   * @param {Position} pointer - The pointer.
   * @return {number} The distance, in px.
   */
  const tipFrom = (run: number, pointer: Position) =>
    track ? pixelsApart(pointOnTrack(track, ring.start + run), pointer) : 0;

  return {
    join: (next, start, entry, pieces) => {
      track = next;
      ring = {
        stops: traceStops(next, pieces).filter(
          (stop) => pixelsApart(pointOnTrack(next, stop), entry) > JOIN_PX
        ),
        total: next.total,
        start,
      };
      shown = 0;
      passed = null;
    },
    settle: (run, pointer) => {
      if (passed !== null && Math.sign(run - passed) !== passedDirection) {
        passed = null;
      }
      for (;;) {
        const stop = stopBetween(ring, shown, run, passed);
        if (stop === null) {
          shown = run;
          return;
        }
        shown = stop;
        if (tipFrom(stop, pointer) <= TRACE_STOP_PX) return;
        passed = stop;
        passedDirection = Math.sign(run - stop) || 1;
      }
    },
    land: (pointer) => {
      const [nearest] = [1, -1]
        .map((direction) =>
          stopBetween(ring, shown, shown + (direction * ring.total) / 2, null)
        )
        .filter(
          (stop): stop is number =>
            stop !== null &&
            Math.abs(stop) < ring.total * 0.98 &&
            tipFrom(stop, pointer) <= TRACE_STOP_PX
        )
        .sort((a, b) => tipFrom(a, pointer) - tipFrom(b, pointer));
      if (nearest !== undefined) shown = nearest;
    },
    shown: () => shown,
  };
}
